import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ApplicationConfig, provideZoneChangeDetection, provideAppInitializer, ErrorHandler, inject } from "@angular/core";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration, withEventReplay } from "@angular/platform-browser";
import { provideRouter } from "@angular/router";
import { routes } from "./app.routes";
import { TokenInterceptor } from "./core/auth/interceptor/token.interceptor";
import { ConfirmationService, MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import Aura from '@primeng/themes/aura';
import { AbstractSecurityStorage, DefaultLocalStorageService, provideAuth, StsConfigLoader, withAppInitializerAuthCheck } from "angular-auth-oidc-client";
import { GlobalErrorHandler } from "./core/error/global-error-handler";
import { ErrorInterceptor } from "./core/error/error.interceptor";
import { DialogService } from "primeng/dynamicdialog";
import { definePreset } from "@primeng/themes";
import { configFactory } from "./core/config/config-service";
import { authConfigLoaderFactory } from "./core/auth/config/oidc-auth.config";
import { BrandingService } from "./core/branding/branding.service";
import { CustomMessageService } from "./core/ui/custom-message.service";


const Noir = definePreset(Aura, {
  color: {
    name: 'noir'
  },
  semantic: {
      primary: {
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}'
      },
      colorScheme: {
          light: {
              primary: {
                  color: '{primary.950}',
                  contrastColor: '#ffffff',
                  hoverColor: '{primary.800}',
                  activeColor: '{primary.700}'
              },
              highlight: {
                  background: '{primary.950}',
                  focusBackground: '{primary.700}',
                  color: '#ffffff',
                  focusColor: '#ffffff'
              },
              surface: {
                  0: '#ffffff',
                  50: '{zinc.50}',
                  100: '{zinc.100}',
                  200: '{zinc.200}',
                  300: '{zinc.300}',
                  400: '{zinc.400}',
                  500: '{zinc.500}',
                  600: '{zinc.600}',
                  700: '{zinc.700}',
                  800: '{zinc.800}',
                  900: '{zinc.900}',
                  950: '{zinc.950}'
              }
          },
          dark: {
              primary: {
                  color: '{primary.50}',
                  contrastColor: '{primary.950}',
                  hoverColor: '{primary.200}',
                  activeColor: '{primary.300}'
              },
              highlight: {
                  background: '{primary.50}',
                  focusBackground: '{primary.300}',
                  color: '{primary.950}',
                  focusColor: '{primary.950}'
              },
              surface: {
                  0: '#ffffff',
                  50: '{zinc.50}',
                  100: '{zinc.100}',
                  200: '{zinc.200}',
                  300: '{zinc.300}',
                  400: '{zinc.400}',
                  500: '{zinc.500}',
                  600: '{zinc.600}',
                  700: '{zinc.700}',
                  800: '{zinc.800}',
                  900: '{zinc.900}',
                  950: '{zinc.950}'
              }
          }
      }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withEventReplay()),
    provideAppInitializer(configFactory),
    provideAppInitializer(() => {
      return inject(BrandingService).load();
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(), 
    { provide: MessageService, useClass: CustomMessageService },
    ConfirmationService,
    DialogService,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideRouter(routes), 
    providePrimeNG({theme: {preset: Noir, options: {darkModeSelector: '.app-dark'}}}),
    
    // Konfiguration der OpenID Connect Authentifizierung mit angular-auth-oidc-client.
    // Die Config wird zur Laufzeit aus assets/config/config.<env>.json geladen (siehe
    // authConfigLoaderFactory), damit ein Profilwechsel ohne Angular-Rebuild wirkt.
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: authConfigLoaderFactory,
        deps: [HttpClient],
      },
    },
    // Prüft beim App-Start automatisch den Authentifizierungsstatus.
    withAppInitializerAuthCheck()
    ),
    /*
        Firefox (v. a. in Strict ETP, Container Tabs oder Privates Fenster) kann beim Cross-Site-Redirect den sessionStorage-Inhalt anders behandeln. 
        Die Library legt die transienten PKCE-Daten (state, codeVerifier, nonce) standardmäßig dort ab. Meldet man sich im Firefox ab und wieder an, kommt ein Fehler.       
    */
    // TODO LIM: Zeile auskommentieren und prüfen ob das Problem im Testsystem noch existiert (da eigentlich kein Cross-Site-Redirect mehr stattfindet).
    { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
  ]
};

